import React from "react";
import classNames from "classnames";
import { Menu, MenuProps, MenuButton, MenuItems, MenuItem } from "@headlessui/react";
import InViewObserver from "@/component/InViewObserver";
import ChevronArrowIcon from "@/component/icon/ChevronArrowIcon";


interface DropdownGroupProps extends Omit<MenuProps, "style"> {
    sm?: boolean;
    width?: number;
    className?: string;
    "aria-label"?: string;
    "aria-labelledby"?: string;
    "data-testid"?: string;
}

const DropdownGroup: React.FC<DropdownGroupProps> = (props) => {
    const {
        sm,
        width,
        children,
        className: propsClassName,
        ...rest
    } = props;

    return (
        <Menu as="div"              className={classNames(
                  "relative flex-none",
                  sm ? "w-dropdown-sm" : "w-full",
                  propsClassName
              )}
              {...rest}
              /** @ts-ignore */
              style={{ width }}
        >
            {children}
        </Menu>
    );
};

interface DropdownButtonProps {
    open?: boolean;
    close?: () => void;
    as?: "button" | "div";
    variant?: "secondary";
    disabled?: boolean;
    invalid?: boolean;
    isSelected?: boolean;
    className?: string;
    iconClassName?: string;
    children: React.ReactNode;
}

export const DropdownButton: React.FC<DropdownButtonProps> = (props) => {
    const {
        open,
        close,
        as = "button",
        variant = "primary",
        disabled,
        invalid,
        isSelected = true,
        className: propsClassName,
        iconClassName,
        children
    } = props;

    return (
        <InViewObserver enabled={open}
                        onHidden={close}
        >{inViewRef => (
            <MenuButton as={as}
                        ref={inViewRef}
                        disabled={disabled}
                        className={classNames(
                            "dropdown-button",
                            !isSelected && "dropdown-button-placeholder",
                            `dropdown-${variant}`,
                            invalid && "input-invalid",
                            propsClassName
                        )}
            >{({ open }) => (<>
                <div className="w-full text-start truncate">
                    {children}
                </div>
                <ChevronArrowIcon direction={open ? "up" : "down"}
                                  className={classNames("flex-none size-4.5", iconClassName)}
                />
            </>)}</MenuButton>
        )}</InViewObserver>
    );
};

interface DropdownItemsProps {
    variant?: "secondary";
    maxHeight?: number;
    className?: string;
    children: React.ReactNode;
}

export const DropdownItems: React.FC<DropdownItemsProps> = (props) => {
    const { children, variant, className: propsClassName, maxHeight } = props;

    const style = maxHeight ? { ["--anchor-max-height" as any]: `${maxHeight}px` } : {};

    return (
        <MenuItems transition
                   anchor="bottom"
                   style={style}
                   className={classNames(
                       "dropdown-items-box w-[var(--button-width)]",
                       propsClassName,
                       variant && `dropdown-${variant}`
                   )}
        >
            {children}
        </MenuItems>
    );
};

interface DropdownItemProps {
    isOdd?: boolean;
    className?: string;
    onClick: (item: any) => void;
    children: React.ReactNode;
}

export const DropdownItem: React.FC<DropdownItemProps> = (props) => {
    const { isOdd, children, onClick, className: propsClassName } = props;

    return (
        <MenuItem as='button'
                  className={classNames("dropdown-item truncate", isOdd && "odd", propsClassName)}
                  onClick={onClick}
        >
            {children}
        </MenuItem>
    );
};

interface DropdownFooterProps {
    className?: string;
    onClick: () => void;
    children: React.ReactNode;
}

export const DropdownFooter: React.FC<DropdownFooterProps> = (props) => {
    const { onClick, children, className: propsClassName } = props;

    return (
        <MenuItem as='button'
                  className={classNames("dropdown-footer", propsClassName)}
                  onClick={onClick}
        >
            {children}
        </MenuItem>
    );
};

export default DropdownGroup;
